import { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import {
  trackCanvasProductOpen,
  trackCanvasConfigure,
  trackCanvasPurchaseCTA,
} from "@/lib/analytics";
import PageMeta from "@/components/PageMeta";
import { fetchProduct, createShopifyCart } from "@/lib/api";
import SectionFadeIn from "@/components/SectionFadeIn";
import ScenePreviewGallery from "@/components/ScenePreviewGallery";
import { getRouteBackTarget } from "@/lib/navigation";
import ImageLightbox from "@/components/ImageLightbox";
import {
  FRAMES, PREVIEW_MODES,
  detectOrientation, getSizes, sizeKey, defaultSize,
  resolveVariant, stripHtml, formatCurrency, getInternalPresetPrice,
} from "@/lib/canvasConfig";
import CanvasPreviewPane from "@/components/canvas/CanvasPreviewPane";
import CanvasSizeFrameSelector from "@/components/canvas/CanvasSizeFrameSelector";
import CanvasPurchaseCTAs from "@/components/canvas/CanvasPurchaseCTAs";

const SHOPIFY_DOMAIN = process.env.REACT_APP_SHOPIFY_STORE_DOMAIN || "srbt1r-qn.myshopify.com";

export default function CanvasProductDetail() {
  const { handle, slug } = useParams();
  const productHandle = handle || slug;
  const location = useLocation();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [previewMode, setPreviewMode] = useState("artwork");
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedFrame, setSelectedFrame] = useState("none");
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [gallerySlots, setGallerySlots] = useState([]);

  useEffect(() => {
    fetchProduct(productHandle)
      .then((d) => {
        setProduct(d.product);
        if (d.product?.title) {
          trackCanvasProductOpen(productHandle, d.product.title);
        }
        // Auto-select Custom size for store configurator products (e.g. custom-canvas-print)
        if (d.product?.handle?.includes("custom")) {
          setSelectedSize("Custom");
        }
      })
      .catch((err) => console.error("CanvasProductDetail: fetch failed", err))
      .finally(() => setLoading(false));
  }, [productHandle]);

  // Fetch presentation gallery slots — use instead of Shopify images when configured
  useEffect(() => {
    if (!product?.id) return;
    fetch(`/api/products/gallery/${product.id}`)
      .then((r) => r.json())
      .then((data) => { if (data.gallery?.length) setGallerySlots(data.gallery); })
      .catch(() => {});
  }, [product?.id]);

  /* loading */
  if (loading) {
    return (
      <div className="py-20 md:py-28 max-w-7xl mx-auto px-6 md:px-12">
        <div className="animate-pulse grid grid-cols-1 md:grid-cols-2 gap-12">
          <div className="aspect-square bg-brand-surface rounded-sm" />
          <div className="space-y-4 py-8">
            <div className="h-5 bg-brand-surface rounded w-1/3" />
            <div className="h-8 bg-brand-surface rounded w-2/3" />
            <div className="h-20 bg-brand-surface rounded w-full mt-6" />
          </div>
        </div>
      </div>
    );
  }

  /* 404 */
  if (!product) {
    return (
      <div className="py-28 text-center max-w-lg mx-auto px-6">
        <h1 className="font-heading text-3xl text-brand-text">Product Not Found</h1>
        <p className="mt-3 text-brand-text-secondary text-[14px]">
          This product may have been removed or the link is incorrect.
        </p>
        <button
          type="button"
          onClick={() => navigate(getRouteBackTarget(location, "/"))}
          className="inline-flex items-center gap-2 mt-6 text-[13px] text-brand-gold font-medium"
          data-testid="detail-not-found-back"
        >
          <ArrowLeft size={14} /> Back to Canvas Collection
        </button>
      </div>
    );
  }

  // Use gallery slots when present (slot-based presentation), fall back to Shopify images
  const images = gallerySlots.length > 0
    ? gallerySlots.map((s) => ({ url: s.asset_url, altText: s.label, slotKey: s.slot_key }))
    : (product.images || []);
  const hasImage = images.length > 0;
  const currentImg = images[activeImg] || images[0];
  const desc = stripHtml(product.description);
  const shopUrl = `https://${SHOPIFY_DOMAIN}/products/${product.handle}`;

  const orientation = detectOrientation(images);
  const sizes = getSizes(orientation);
  const activeSizeKey = selectedSize || defaultSize(orientation);
  const isCustomSize = activeSizeKey === "Custom";
  const isStoreConfiguratorProduct = product.handle?.includes("custom");

  // resolve size/frame objects for variant matching
  const activeSizeObj = sizes.find((s) => sizeKey(s) === activeSizeKey) || null;
  const activeFrameObj = FRAMES.find((f) => f.id === selectedFrame) || FRAMES[0];
  // attempt variant match
  const matchedVariant = resolveVariant(
    product.variants,
    activeSizeObj?.shopifyOption,
    activeFrameObj?.shopifyOption
  );
  const isVariantUnavailable = matchedVariant && matchedVariant.availableForSale === false;

  const getPresetPriceData = (sizeObj, frameObj = activeFrameObj) => {
    if (!sizeObj) return null;

    // Prefer live Shopify variant price when a variant matches this size+frame combo
    const variantForSize = resolveVariant(
      product.variants,
      sizeObj.shopifyOption,
      frameObj?.shopifyOption
    );
    if (variantForSize) {
      const variantAmount = parseFloat(variantForSize.price?.amount || "0");
      if (variantAmount > 0) {
        return {
          amount: variantAmount,
          currencyCode: variantForSize.price?.currencyCode || "GBP",
          source: "variant",
          available: variantForSize.availableForSale !== false,
        };
      }
    }

    // Fall back to internal preset price table
    const internalAmount = getInternalPresetPrice(
      orientation,
      sizeObj,
      frameObj?.id || "none"
    );
    if (internalAmount !== null) {
      return {
        amount: internalAmount,
        currencyCode: "GBP",
        source: "internal",
        available: null, // no real variant — availability unknown
      };
    }

    return null;
  };

  const activePresetPrice = !isCustomSize ? getPresetPriceData(activeSizeObj) : null;
  const priceAmount = activePresetPrice?.amount || 0;
  const priceCurrencyCode = activePresetPrice?.currencyCode || "GBP";
  const isPriceOnRequest = isCustomSize || priceAmount === 0;
  const priceStr = isPriceOnRequest
    ? "Price on request"
    : formatCurrency(priceAmount, priceCurrencyCode);
  const pricingSource = isCustomSize ? "custom" : (activePresetPrice?.source || "internal");

  // cart url: fallback direct link
  const directCartUrl = matchedVariant
    ? `https://${SHOPIFY_DOMAIN}/cart/${matchedVariant.id}:1`
    : shopUrl;

  const handleCheckout = async () => {
    if (isVariantUnavailable || isCustomSize || checkoutLoading) return;
    setCheckoutLoading(true);
    trackCanvasPurchaseCTA(
      productHandle,
      activeSizeObj?.shopifyOption || "unknown",
      activeFrameObj?.shopifyOption || "Unframed",
      priceStr,
      matchedVariant?.id
    );

    // No matched variant — go to Shopify product page directly
    if (!matchedVariant) {
      window.open(shopUrl, "_blank", "noopener,noreferrer");
      setCheckoutLoading(false);
      return;
    }

    try {
      const result = await createShopifyCart({
        variantId: matchedVariant.id,
        quantity: 1,
        handle: product.handle,
      });
      if (result.checkout_url) {
        window.open(result.checkout_url, "_blank", "noopener,noreferrer");
      } else {
        window.open(directCartUrl, "_blank", "noopener,noreferrer");
      }
    } catch (err) {
      console.error("[CanvasProductDetail] cart creation failed, using direct URL:", err);
      window.open(directCartUrl, "_blank", "noopener,noreferrer");
    } finally {
      setCheckoutLoading(false);
    }
  };
  const selectedPreviewLabel =
    PREVIEW_MODES.find((mode) => mode.id === previewMode)?.label || "Artwork";
  const selectedSizeLabel = isCustomSize
    ? "Custom size"
    : activeSizeObj
      ? `${activeSizeObj.w} \u00d7 ${activeSizeObj.h} cm`
      : activeSizeKey.replace("\u00d7", " \u00d7 ");
  const selectedFrameLabel =
    FRAMES.find((frame) => frame.id === selectedFrame)?.label || "Unframed";
  const purchaseLabel = isVariantUnavailable
    ? "Currently Unavailable"
    : "Complete Purchase on Store";

  const purchaseSupportCopy = (() => {
    if (isCustomSize) {
      return "Custom size stays quote-based so we can confirm artwork scale, finish, and final production details before pricing.";
    }
    if (isVariantUnavailable) {
      return "This size and frame combination is currently unavailable on the store. You can request a quote for production instead.";
    }
    if (pricingSource === "variant") {
      return "Price shown is live from your Shopify store for this size and frame combination. Proceed to the store to complete the purchase securely.";
    }
    return "Prices follow the fixed canvas price ladder. Frame finishes add a flat surcharge. Custom size stays quote-based.";
  })();

  const nextStepCopy = (() => {
    if (isCustomSize) {
      return "Custom size stays quote-based. Send the size and finish details and we'll confirm production and pricing before proceeding.";
    }
    if (isVariantUnavailable) {
      return "This combination is currently out of stock on the store. Request a quote and we can check production availability for you.";
    }
    return "Once your preview, size, and frame feel right, continue to the live store page to complete the purchase.";
  })();

  const priceClassName = (() => {
    if (isPriceOnRequest) return "text-[16px] text-brand-gold italic";
    if (isVariantUnavailable) return "text-[22px] text-brand-text-secondary/40 font-medium line-through";
    return "text-[22px] text-brand-text font-medium";
  })();
  const customQuoteParams = new URLSearchParams();
  customQuoteParams.set("type", "Canvas Print");
  customQuoteParams.set("dimensions", isCustomSize ? "Custom size" : selectedSizeLabel);
  customQuoteParams.set(
    "description",
    `Canvas print enquiry for ${product.title}. Size: ${isCustomSize ? "Custom size" : selectedSizeLabel}. Frame: ${selectedFrameLabel}.`
  );
  const customQuoteHref = `/request-quote?${customQuoteParams.toString()}`;

  /* frame style for preview */
  const frameStyle = () => {
    if (previewMode === "artwork" || selectedFrame === "none") return "";
    if (selectedFrame === "black")
      return "ring-[6px] ring-[#1a1a1a] shadow-[0_0_0_8px_#0d0d0d,0_4px_20px_rgba(0,0,0,0.5)]";
    if (selectedFrame === "natural")
      return "ring-[6px] ring-[#8B7355] shadow-[0_0_0_8px_#6B5B3E,0_4px_20px_rgba(0,0,0,0.4)]";
    return "";
  };

  return (
    <div className="py-16 md:py-24" data-testid={`product-detail-${handle}`}>
      {product && (
        <PageMeta
          title={product.title}
          description={`${product.title} — gallery-quality canvas print available in landscape, portrait, and square sizes. Archival inks on premium stretched canvas. Optional solid wood framing. Configure and order from AM Decor Signs & Prints.`}
        />
      )}
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* breadcrumb */}
        <SectionFadeIn>
          <button
            type="button"
            onClick={() => navigate(getRouteBackTarget(location, "/"))}
            className="inline-flex items-center gap-1.5 text-[11px] text-brand-text-secondary/50 hover:text-brand-gold transition-colors duration-300 tracking-wide uppercase mb-8"
            data-testid="detail-back-link"
          >
            <ArrowLeft size={12} /> Back
          </button>
        </SectionFadeIn>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14">
          {/* ── LEFT: PREVIEW ── */}
          <SectionFadeIn>
            <CanvasPreviewPane
              product={product}
              images={images}
              activeImg={activeImg}
              setActiveImg={setActiveImg}
              previewMode={previewMode}
              setPreviewMode={setPreviewMode}
              selectedFrame={selectedFrame}
              setSelectedFrame={setSelectedFrame}
              onOpenLightbox={setLightboxIndex}
              desc={desc}
              frameStyle={frameStyle}
            />
          </SectionFadeIn>

          {/* ── RIGHT: INFO ── */}
          <SectionFadeIn delay={80}>
            <div className="py-2">
              <p className="text-[11px] tracking-[0.25em] uppercase font-semibold text-brand-gold mb-2">
                {product.productType || "Canvas Print"}
              </p>

              <h1
                className="font-heading text-3xl sm:text-[2.5rem] tracking-tight text-brand-text leading-[1.1]"
                data-testid="product-title"
              >
                {product.title}
              </h1>

              <div className="mt-4 flex items-baseline gap-3">
                <p
                  className={priceClassName}
                  data-testid="product-price"
                >
                  {priceStr}
                </p>
                {pricingSource === "variant" && !isPriceOnRequest && (
                  <span
                    className={`text-[10px] uppercase tracking-[0.16em] ${
                      isVariantUnavailable ? "text-brand-text-secondary/35" : "text-green-400/70"
                    }`}
                    data-testid="product-price-source-live"
                  >
                    {isVariantUnavailable ? "Unavailable" : "Live Shopify price"}
                  </span>
                )}
              </div>

              {/* description */}
              {desc && (
                <p className="mt-5 text-[14px] text-brand-text-secondary leading-relaxed">
                  {desc.length > 300 ? desc.slice(0, 300) + "\u2026" : desc}
                </p>
              )}

              <div
                className="mt-6 rounded-sm border border-brand-border bg-brand-surface/35 p-5"
                data-testid="canvas-selection-summary"
              >
                <p className="text-[11px] tracking-[0.18em] uppercase font-semibold text-brand-gold">
                  Current Selection
                </p>
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {[
                    { label: "Preview", value: selectedPreviewLabel },
                    { label: "Size", value: selectedSizeLabel },
                    { label: "Frame", value: selectedFrameLabel },
                  ].map((item) => (
                    <div
                      key={item.label}
                      className="rounded-sm border border-white/[0.05] bg-brand-bg/70 px-4 py-3"
                      data-testid={`canvas-selection-${item.label.toLowerCase()}`}
                    >
                      <p className="text-[10px] uppercase tracking-[0.16em] text-brand-text-secondary/45">
                        {item.label}
                      </p>
                      <p className="mt-2 text-[14px] font-medium text-brand-text">
                        {item.value}
                      </p>
                    </div>
                  ))}
                </div>
                <p
                  className="mt-4 text-[12px] text-brand-text-secondary/60 leading-relaxed"
                  data-testid="canvas-selection-support-copy"
                >
                  {purchaseSupportCopy}
                </p>
              </div>

              {/* ── SIZE + FRAME ── */}
              <CanvasSizeFrameSelector
                product={product}
                sizes={sizes}
                activeSizeKey={activeSizeKey}
                setSelectedSize={setSelectedSize}
                selectedFrame={selectedFrame}
                setSelectedFrame={setSelectedFrame}
                previewMode={previewMode}
                setPreviewMode={setPreviewMode}
                activeSizeObj={activeSizeObj}
                isCustomSize={isCustomSize}
                hasImage={hasImage}
                getPresetPriceData={getPresetPriceData}
              />

              {/* ── CTAs + Next Step + Trust ── */}
              <CanvasPurchaseCTAs
                location={location}
                handle={handle}
                nextStepCopy={nextStepCopy}
                isCustomSize={isCustomSize}
                isVariantUnavailable={isVariantUnavailable}
                isPriceOnRequest={isPriceOnRequest}
                checkoutLoading={checkoutLoading}
                purchaseLabel={purchaseLabel}
                customQuoteHref={customQuoteHref}
                onCheckout={handleCheckout}
                selectedFrame={selectedFrame}
              />
            </div>
          </SectionFadeIn>
        </div>
      </div>

      {/* ── Scene Preview Gallery ── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
        <ScenePreviewGallery productId={product.id} />
      </div>

      {lightboxIndex !== null && (
        <ImageLightbox
          images={images}
          activeIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onPrev={() => setLightboxIndex((current) => (current - 1 + images.length) % images.length)}
          onNext={() => setLightboxIndex((current) => (current + 1) % images.length)}
          title={product.title}
          testIdPrefix="canvas-lightbox"
        />
      )}
    </div>
  );
}
